import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Typography,
  useTheme,
  Breadcrumbs,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import NotificationsIcon from "@mui/icons-material/Notifications";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import Sidebar from "./SideBar";
import Logo from "../../assets/logo.svg";
import darklogo from "../../assets/spark_logo_dark.svg";
import { useAuth } from "../../services/auth/AuthContext";
import { ThemeContext } from "../../ThemeContext";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useNavigate, useLocation } from "react-router-dom";
import Loading from "../loader/loading";
import PersonIcon from "@mui/icons-material/Person";
import { getBreadCrumbs } from "../../routes/generate_breadcrumbs";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { get } from "lodash";
import axiosInstance from "../../services/axiosInstance";
import { SA_USER_PROFILE, SA_USER_SIGNOUT } from "../../shared/constants/apiEndpoints";
import { toast } from "react-toastify";

const drawerWidth = 240;
const HEADER_HEIGHT = 60;

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const { logout } = useAuth();
  const themeContext = useContext(ThemeContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoader, setIsLoader] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [matchBrowser, setMatchBrowser] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);

  const toggleDrawer = () => {
    setOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = async () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
  };

  const handleAccount = () => {
    navigate("/account");
    handleMenuClose();
  };

  const getProfile = async () => {
    try {
      const response = await axiosInstance.get(SA_USER_PROFILE);
      if (response.data.status === 200) {
        setProfileData(response?.data?.data);
      }
    } catch (error) {
      toast.error("Error fetching notification counts");
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const logoout=async() => {
    try{
      const response=await axiosInstance.post(SA_USER_SIGNOUT); 
      if(response.data.status === 200) {
        toast.success("Logged out successfully");
        navigate("/login");
      }
    } catch (error) {
      toast.error("Error logging out");
    }
  }

  // Use shared breadcrumb generator
  const crumbs = getBreadCrumbs(location.pathname, navigate);

  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          bgcolor: theme.palette.background.paper,
          height: `${HEADER_HEIGHT}px`,
          zIndex: 1000,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pb: 0,
            color: theme.palette.text.primary,
            minHeight: `${HEADER_HEIGHT}px !important`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              onClick={() => setOpen(true)}
              sx={{ mr: 2, color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src={theme.palette.mode === "dark" ? darklogo : Logo}
              alt="logo"
              style={{ width: "100px", height: "35px", cursor: "pointer" }}
              onClick={() => navigate("/organisation")}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: "35px", borderColor: "grey.400", mt: 0, pl: 2 }}
            />
            <Breadcrumbs aria-label="breadcrumb">
              {crumbs.map((breadcrumb, index) => (
                <Typography
                  key={index}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "20px",
                    ml: 3,
                    color: "text.primary",
                    cursor: breadcrumb.onClick ? "pointer" : "default",
                  }}
                  variant="h6"
                  component="div"
                  onClick={breadcrumb.onClick}
                >
                  {breadcrumb.text}
                </Typography>
              ))}
            </Breadcrumbs>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                borderRadius: "24px",
                px: 1,
                py: 0.5,
                transition: "background 0.2s",
                "&:hover": {
                  background:
                    theme.palette.mode === "dark" ? "#232334" : "#F4F4F7",
                },
                zIndex: 999,
              }}
            >
              <Avatar
                src={undefined} // Replace with user image if available
                sx={{ width: 40, height: 40, mr: 1 }}
              >
                <PersonIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mr: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: theme.palette.text.primary,
                    lineHeight: 1,
                    mb: 1,
                  }}
                >
                  {profileData?.full_name}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.92rem",
                    color: isDarkMode ? "#fff" : "#667085",
                    lineHeight: 1,
                  }}
                >
                  {profileData?.role}{" "}
                </Typography>
              </Box>
              <ArrowDropDownIcon
                sx={{ color: theme.palette.text.secondary, fontSize: 24 }}
              />
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 220,
                  mt: 1,
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Tooltip title={profileData?.email || ""} arrow>
                <Typography
                  sx={{
                    fontWeight: "bolder",
                    fontSize: "15px",
                    width: "90%",
                    mb: 1,
                    pt: 2,
                    pl: 2,
                    whiteSpace: "nowrap", // keep in one line
                    overflow: "hidden",
                    cursor: "default", // hide overflow text
                    textOverflow: "ellipsis", // add "..."
                  }}
                >
                  {profileData?.email}
                </Typography>
              </Tooltip>

              <MenuItem onClick={handleAccount}>
                {/* <Typography variant="body2">Account</Typography> */}
              </MenuItem>
              <MenuItem
                disableRipple
                sx={{
                  display: "block",
                  pt: 2,
                  pb: 2,
                  cursor: "default",
                  "&:hover": { background: "none" },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      bgcolor:
                        theme.palette.mode === "dark" ? "#2D2D2D" : "#F0F0FF",
                      p: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (themeContext && themeContext.darkMode)
                          themeContext.toggleTheme();
                        handleMenuClose();
                      }}
                      sx={{
                        flex: 1,
                        bgcolor: !themeContext?.darkMode
                          ? "primary.main"
                          : "transparent",
                        color: !themeContext?.darkMode
                          ? "white"
                          : theme.palette.mode === "dark"
                            ? "#fff"
                            : "#696595",
                        "&:hover": {
                          bgcolor: !themeContext?.darkMode
                            ? "primary.dark"
                            : theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.04)",
                        },
                        textTransform: "none",
                        py: 0.5,
                      }}
                      startIcon={<LightModeIcon />}
                    >
                      Light
                    </Button>
                    <Button
                      onClick={() => {
                        if (themeContext && !themeContext.darkMode)
                          themeContext.toggleTheme();
                        handleMenuClose();
                      }}
                      sx={{
                        flex: 1,
                        bgcolor: themeContext?.darkMode
                          ? "primary.main"
                          : "transparent",
                        color: themeContext?.darkMode
                          ? "white"
                          : theme.palette.mode === "dark"
                            ? "#fff"
                            : "#696595",
                        "&:hover": {
                          bgcolor: themeContext?.darkMode
                            ? "primary.dark"
                            : theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.04)",
                        },
                        textTransform: "none",
                        py: 0.5,
                      }}
                      startIcon={<DarkModeIcon />}
                    >
                      Dark
                    </Button>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={matchBrowser}
                        onChange={(e) => {
                          setMatchBrowser(e.target.checked);
                          if (e.target.checked && themeContext) {
                            const prefersDark = window.matchMedia(
                              "(prefers-color-scheme: dark)"
                            ).matches;
                            if (prefersDark !== themeContext.darkMode) {
                              themeContext.toggleTheme();
                            }
                          }
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#a0a0a0"
                              : "#696595",
                        }}
                      >
                        Match Browser
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Typography variant="body2">Sign Out</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Layout: sidebar + main content */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          pt: `${HEADER_HEIGHT}px`,
        }}
      >
        {/* Sidebar (permanent/temporary handled inside Sidebar) */}
        <Sidebar isOpen={open} closeSidebar={toggleDrawer} />
        {/* Main content area */}
        <Box
          component="main"
          className="main-content-scrollbar"
          sx={{
            flex: 1,
            minHeight: 0,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflowY: "auto",
            overflowX: "hidden",
            bgcolor:
              theme.palette.mode === "dark" ? "background.default" : "#fbfbff",
            p: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Only show scroll if content overflows */}
          <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
        </Box>
      </Box>
      <Loading show={isLoader} />
    </Box>
  );
};

export default AppLayout;
